// src/hooks/useClientPresence.ts
// «Кто подключён к роботу»: пока открыта страница робота с сессией Zenoh,
// она держит liveliness-токен robot/<id>/clients/<user>/<random>. Токен
// пропадает сам, когда вкладку закрыли или связь оборвалась. Робот
// (fleet_adapter) по этим токенам считает клиентов и засыпает, когда никого
// нет; страница считает их сама — чтобы видеть число и без робота.
import { useEffect, useRef } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';

// Символы, недопустимые в части ключа Zenoh (и разделитель), заменяем на «_».
const keyChunk = (text: string) => text.replace(/[/*$?#\s]/g, '_') || '_';

export interface Presence {
  count: number;
  users: Record<string, number>;  // пользователь -> сколько у него открытых страниц
}

function summarize(tokens: Map<string, string>): Presence {
  const users: Record<string, number> = {};
  tokens.forEach(u => { users[u] = (users[u] ?? 0) + 1; });
  return { count: tokens.size, users };
}

export function useClientPresence(
  session: zenoh.Session | null,
  robotId: string,
  user: string | null,
  onChange: (presence: Presence) => void,
) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const tokens = new Map<string, string>();  // ключ токена -> пользователь
    onChangeRef.current(summarize(tokens));
    if (!session) return;
    let cancelled = false;
    let token: zenoh.LivelinessToken | null = null;
    let subscriber: zenoh.Subscriber | null = null;
    const prefix = `robot/${robotId}/clients/`;
    const id = Math.random().toString(36).slice(2, 10);
    const liveliness = session.liveliness();

    liveliness
      .declareSubscriber(`${prefix}**`, {
        history: true,
        handler: (sample: zenoh.Sample) => {
          if (cancelled) return;
          const key = sample.keyexpr().toString();
          if (sample.kind() === zenoh.SampleKind.DELETE) tokens.delete(key);
          else tokens.set(key, key.slice(prefix.length).split('/')[0] || '?');
          onChangeRef.current(summarize(tokens));
        },
      })
      .then(sub => {
        if (cancelled) sub.undeclare().catch(() => {});
        else subscriber = sub;
      })
      .catch(err => console.warn('clients subscription failed', err));
    liveliness
      .declareToken(`${prefix}${keyChunk(user ?? 'anon')}/${id}`)
      .then(t => {
        if (cancelled) t.undeclare().catch(() => {});
        else token = t;
      })
      .catch(err => console.warn('client token failed', err));

    return () => {
      cancelled = true;
      token?.undeclare().catch(() => {});
      subscriber?.undeclare().catch(() => {});
    };
  }, [session, robotId, user]);
}
